import { Gauge, Loader2, RotateCcw, ThumbsDown, Users, Wallet } from "lucide-react"
import { useEffect, useState } from "react"

import { Page } from "@/components/layout/page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { api } from "@/lib/api"
import type { OperatorFeedback, OperatorLimits, OperatorOverview, Tally } from "@/lib/types"
import { cn, formatRelative, formatUsd } from "@/lib/utils"

/**
 * The operator's page: what PRAXA costs, what people think of its answers, and
 * the limits that keep it free.
 *
 * Only the person named in `CC_OPERATOR_EMAILS` reaches it - the server
 * refuses everyone else, whatever this page does. People appear as pseudonyms
 * (`user-1a2b3c4d`), never by e-mail address: the operator needs to see that
 * one account spends a lot, not who it is.
 *
 * The idea came from a fellow student's demo. The monthly ceiling is shown but
 * not editable, on purpose: a budget that can be raised from inside the
 * application it restrains is not a budget.
 */
export function OperatorRoute() {
  const [overview, setOverview] = useState<OperatorOverview | null>(null)
  const [feedback, setFeedback] = useState<OperatorFeedback | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void Promise.all([api.operator.overview(), api.operator.feedback()])
      .then(([nextOverview, nextFeedback]) => {
        if (cancelled) return
        setOverview(nextOverview)
        setFeedback(nextFeedback)
      })
      .catch((reason: Error) => !cancelled && setError(reason.message))
    return () => {
      cancelled = true
    }
  }, [])

  function limitsChanged(limits: OperatorLimits) {
    setOverview((current) => (current ? { ...current, limits } : current))
  }

  return (
    <Page
      tone="developer"
      plain
      eyebrow="Operator"
      title="Running PRAXA"
      description="Spending, ratings and the limits that keep it free – across every account."
    >
      <div className="space-y-5 px-5 py-6 lg:px-8">
        {error ? (
          <p className="rounded-lg border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
            {error}
          </p>
        ) : null}

        {overview === null && error === null ? (
          <div className="grid gap-4 md:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-24" />
            ))}
          </div>
        ) : null}

        {overview ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Stat
                icon={Wallet}
                label="Spent today"
                value={formatUsd(overview.costTodayUsd)}
                note={`${overview.callsToday} model calls`}
              />
              <MonthStat overview={overview} />
              <Stat
                icon={Users}
                label="Accounts"
                value={overview.registeredAccounts === null ? "–" : String(overview.registeredAccounts)}
                note={`${overview.activeToday} active today · ${overview.activeMonth} this month`}
              />
              <Stat
                icon={ThumbsDown}
                label="Rated unhelpful"
                value={feedback ? `${Math.round(feedback.unhelpfulShare * 100)}%` : "–"}
                note={feedback ? `${feedback.votes} ratings in ${feedback.days} days` : ""}
              />
            </div>

            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
              <AccountsTable overview={overview} />
              <LimitsPanel limits={overview.limits} onSaved={limitsChanged} />
            </div>
          </>
        ) : null}

        {feedback ? <FeedbackPanel feedback={feedback} /> : null}
      </div>
    </Page>
  )
}

function Stat({
  icon: Icon,
  label,
  value,
  note,
}: {
  icon: typeof Wallet
  label: string
  value: string
  note: string
}) {
  return (
    <div className="rounded-2xl border bg-card/50 p-4">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <Icon className="size-3.5" aria-hidden />
        {label}
      </p>
      <p className="tabular mt-2 text-2xl font-semibold">{value}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>
    </div>
  )
}

function MonthStat({ overview }: { overview: OperatorOverview }) {
  const cap = overview.monthlyBudgetUsd
  const spent = overview.monthToDateUsd
  const share = cap > 0 ? Math.min(spent / cap, 1) : 0
  return (
    <div className="rounded-2xl border bg-card/50 p-4">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        <Gauge className="size-3.5" aria-hidden />
        This month
      </p>
      <p className="tabular mt-2 text-2xl font-semibold">
        {formatUsd(spent)}
        <span className="text-sm font-normal text-muted-foreground"> / {formatUsd(cap)}</span>
      </p>
      <div
        className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={Math.round(share * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Monthly ceiling used"
      >
        <div
          className={cn(
            "h-full rounded-full",
            share >= 0.9 ? "bg-destructive" : share >= 0.7 ? "bg-warning" : "bg-primary",
          )}
          style={{ width: `${share * 100}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Ceiling set in the deployment, not here</p>
    </div>
  )
}

function AccountsTable({ overview }: { overview: OperatorOverview }) {
  return (
    <section className="rounded-2xl border bg-card/50 p-4">
      <h2 className="font-semibold">Accounts this month</h2>
      <p className="text-xs text-muted-foreground">
        By pseudonym, most expensive first. Economy marks an account past the threshold today.
      </p>
      {overview.accounts.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No model calls this month yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Account</th>
                <th className="py-1.5 pr-3 text-right font-medium">Calls today</th>
                <th className="py-1.5 pr-3 text-right font-medium">Today</th>
                <th className="py-1.5 pr-3 text-right font-medium">Month</th>
                <th className="py-1.5 font-medium">Last active</th>
              </tr>
            </thead>
            <tbody>
              {overview.accounts.map((use) => (
                <tr key={use.who} className="border-t border-border/50">
                  <td className="py-1.5 pr-3 font-mono text-xs">
                    {use.who}
                    {use.inEconomy ? (
                      <Badge variant="outline" className="ml-2">
                        economy
                      </Badge>
                    ) : null}
                  </td>
                  <td className="tabular py-1.5 pr-3 text-right">
                    {use.callsToday} / {overview.limits.dailyModelCalls}
                  </td>
                  <td className="tabular py-1.5 pr-3 text-right">{formatUsd(use.costTodayUsd)}</td>
                  <td className="tabular py-1.5 pr-3 text-right">{formatUsd(use.costMonthUsd)}</td>
                  <td className="py-1.5 text-xs text-muted-foreground">{use.lastActive}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * The three numbers the operator can change without a redeploy. Saved as a
 * row in the database, so every server instance applies the same limits from
 * the next call on.
 */
function LimitsPanel({
  limits,
  onSaved,
}: {
  limits: OperatorLimits
  onSaved: (limits: OperatorLimits) => void
}) {
  const [calls, setCalls] = useState(String(limits.dailyModelCalls))
  const [percent, setPercent] = useState(String(limits.economyAtPercent))
  const [model, setModel] = useState(limits.economyModel)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  async function save(reset: boolean) {
    setBusy(true)
    setNote(null)
    try {
      const saved = await api.operator.setLimits(
        reset
          ? {}
          : {
              dailyModelCalls: Number(calls),
              economyAtPercent: Number(percent),
              economyModel: model,
            },
      )
      setCalls(String(saved.dailyModelCalls))
      setPercent(String(saved.economyAtPercent))
      setModel(saved.economyModel)
      onSaved(saved)
      setNote(reset ? "Back to the deployment settings." : "Saved – applies from the next call.")
    } catch (error) {
      setNote((error as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const callsNumber = Number(calls)
  const percentNumber = Number(percent)
  const valid =
    Number.isInteger(callsNumber) &&
    callsNumber >= 1 &&
    callsNumber <= 5000 &&
    Number.isInteger(percentNumber) &&
    percentNumber >= 1 &&
    percentNumber <= 100
  const from = valid && percentNumber < 100 ? Math.ceil((callsNumber * percentNumber) / 100) : null

  return (
    <section className="space-y-3 rounded-2xl border bg-card/50 p-4">
      <div>
        <h2 className="font-semibold">Limits per account</h2>
        <p className="text-xs text-muted-foreground">
          From the threshold, an account&apos;s calls use the cheaper model instead of stopping. At
          the allowance, it is &quot;come back tomorrow&quot;.
        </p>
      </div>

      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">Model calls per day</span>
        <Input
          type="number"
          min={1}
          max={5000}
          value={calls}
          onChange={(event) => setCalls(event.target.value)}
        />
      </label>
      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">Economy from (% of the day)</span>
        <Input
          type="number"
          min={1}
          max={100}
          value={percent}
          onChange={(event) => setPercent(event.target.value)}
        />
      </label>
      <label className="block space-y-1 text-sm">
        <span className="text-muted-foreground">Economy model</span>
        <select
          value={model}
          onChange={(event) => setModel(event.target.value)}
          className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
        >
          {limits.models.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      </label>

      <p className="text-xs text-muted-foreground">
        {from === null
          ? "Economy mode off."
          : `The first ${from} calls of a day use the chosen model; the rest, up to ${callsNumber}, use ${model}.`}{" "}
        The critic always keeps its own model.
      </p>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => void save(false)} disabled={!valid || busy}>
          {busy ? <Loader2 className="animate-spin" aria-hidden /> : null}
          Save
        </Button>
        <Button
          variant="outline"
          onClick={() => void save(true)}
          disabled={busy || limits.overridden.length === 0}
        >
          <RotateCcw aria-hidden />
          Use deployment settings
        </Button>
      </div>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
      <p className="text-xs text-muted-foreground">
        Deployment settings: {limits.defaults.dailyModelCalls} calls, economy from{" "}
        {limits.defaults.economyAtPercent}% to {limits.defaults.economyModel}.
      </p>
    </section>
  )
}

function FeedbackPanel({ feedback }: { feedback: OperatorFeedback }) {
  return (
    <section className="rounded-2xl border bg-card/50 p-4">
      <h2 className="font-semibold">What people think of the answers</h2>
      <p className="text-xs text-muted-foreground">
        Thumbs from the last {feedback.days} days, across {feedback.accounts} account
        {feedback.accounts === 1 ? "" : "s"}. Questions and answers were masked for personal data
        before they were stored.
      </p>

      {feedback.votes === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No ratings yet.</p>
      ) : (
        <div className="mt-4 grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <div className="space-y-4">
            <TallyBars title="By mode" rows={feedback.byMode} />
            <TallyBars title="By model" rows={feedback.byModel} />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Latest unhelpful answers</p>
            {feedback.recentUnhelpful.length === 0 ? (
              <p className="text-sm text-muted-foreground">None – every rating was a thumbs-up.</p>
            ) : (
              <ul className="space-y-2">
                {feedback.recentUnhelpful.map((item) => (
                  <li key={`${item.who}-${item.createdAt}`} className="rounded-xl border bg-background/40 p-3 text-sm">
                    <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{item.who}</span>
                      <Badge variant="outline">{item.mode}</Badge>
                      <span className="font-mono">{item.model}</span>
                      <span>{formatRelative(item.createdAt)}</span>
                    </p>
                    <p className="mt-1.5 font-medium">{item.question}</p>
                    <p className="mt-1 text-muted-foreground">{item.answer}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

function TallyBars({ title, rows }: { title: string; rows: Tally[] }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {rows.map((row) => {
        const share = row.votes > 0 ? row.unhelpful / row.votes : 0
        return (
          <div key={row.name} className="space-y-1">
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="truncate font-mono">{row.name}</span>
              <span className="tabular text-muted-foreground">
                {row.unhelpful} of {row.votes} unhelpful
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
              <div className="h-full rounded-full bg-warning" style={{ width: `${share * 100}%` }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
